import { inject, Injectable } from '@angular/core';
import { CrmWorkspacesStore } from '@pinnakl/crm/data-access';
import { firstValueFrom } from 'rxjs';
import { OUTLOOK_AUTO_SAVE_WORKSPACE_KEY, OutlookAutoSaveWorkspace } from '../../auto-save/outlook-auto-save.models';
import { OFFICE_CONTEXT } from '../office/office.config';
import { RoamingSettingsService } from './roaming-settings.service';
import { UserService } from './user.service';

const CONFIRMED_SELECTION_KEY = 'outlookConfirmedWorkspace';

@Injectable({ providedIn: 'root' })
export class OutlookWorkspaceService {
  private readonly workspaces = inject(CrmWorkspacesStore);
  private readonly roamingSettings = inject(RoamingSettingsService);
  private readonly settings = inject(OFFICE_CONTEXT).roamingSettings;
  private readonly userService = inject(UserService);
  private restoredFor?: string;
  private initialSettings = 'null';

  public restoreSelection(): void {
    const user = this.userService.getUser();
    if (!user) return;
    const identity = `${user.user.clientId}:${user.user.id}`;
    if (this.restoredFor === identity) return;
    this.restoredFor = identity;
    const saved = this.roamingSettings.getItem<OutlookAutoSaveWorkspace>(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY);
    this.initialSettings = JSON.stringify(saved);
    let selected = saved;
    try {
      const confirmed = JSON.parse(sessionStorage.getItem(CONFIRMED_SELECTION_KEY) ?? 'null') as {
        pending?: boolean;
        initial: string;
        selected: OutlookAutoSaveWorkspace;
      } | null;
      if (confirmed && !confirmed.pending && confirmed.initial === JSON.stringify(saved)) selected = confirmed.selected;
      else sessionStorage.removeItem(CONFIRMED_SELECTION_KEY);
    } catch {
      sessionStorage.removeItem(CONFIRMED_SELECTION_KEY);
    }
    if (
      selected?.userId === String(user.user.id) &&
      selected.clientId === String(user.user.clientId) &&
      Number.isInteger(selected.workspaceId)
    ) {
      this.settings.set(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY, selected);
      this.workspaces.selectWorkspace(selected.workspaceId);
    }
  }

  public async persistSelection(workspaceId: number): Promise<void> {
    const user = this.userService.getUser();
    if (!user) throw new Error('An Outlook login is required.');
    const selected: OutlookAutoSaveWorkspace = {
      userId: String(user.user.id),
      clientId: String(user.user.clientId),
      workspaceId,
    };
    const previous = this.roamingSettings.getItem<OutlookAutoSaveWorkspace>(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY);
    if (JSON.stringify(previous) === JSON.stringify(selected)) return;
    const cached = sessionStorage.getItem(CONFIRMED_SELECTION_KEY);
    sessionStorage.setItem(
      CONFIRMED_SELECTION_KEY,
      JSON.stringify({ pending: true, initial: this.initialSettings, selected }),
    );
    try {
      await firstValueFrom(this.roamingSettings.setItem(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY, selected));
    } catch (error) {
      if (previous === null) this.settings.remove(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY);
      else this.settings.set(OUTLOOK_AUTO_SAVE_WORKSPACE_KEY, previous);
      if (cached === null) sessionStorage.removeItem(CONFIRMED_SELECTION_KEY);
      else sessionStorage.setItem(CONFIRMED_SELECTION_KEY, cached);
      throw error;
    }
    sessionStorage.setItem(CONFIRMED_SELECTION_KEY, JSON.stringify({ initial: this.initialSettings, selected }));
  }
}
